import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from './company/Header';
import { 
  User, 
  Truck, 
  Wrench, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Trash2, 
  ZoomIn, 
  ZoomOut 
} from 'lucide-react';
import moment from 'moment';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { v4 as uuidv4 } from 'uuid';

import EditAssignmentModal from './EditAssignmentModal'; 
import AddResourceModal from './AddResourceModal';
import NotificationContainer from './NotificationContainer';
import ResourceInfoPopup from './ResourceInfoPopup';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;
const DAY_WIDTH = 100;

const assignmentTypeDetails = {
  project: {
    label: (project) => project?.name || 'Projekt',
    color: (project) => project?.color || '#3498db',
  },
  travel: {
    label: () => 'Travel',
    color: () => '#95a5a6',
  },
  holiday: {
    label: () => 'Holiday',
    color: () => '#34495e',
  },
  day_off: {
    label: () => 'Day Off',
    color: () => '#f39c12',
  },
};

const getFullWeeksDays = (startDate, endDate) => {
  const start = moment(startDate).startOf('isoWeek');
  const end = moment(endDate).endOf('isoWeek');
  return end.diff(start, 'days') + 1;
};

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [projects, setProjects] = useState([]); 
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentDate, setCurrentDate] = useState(moment().startOf('isoWeek'));
  const [viewMode, setViewMode] = useState('2 Weeks');
  const [filter, setFilter] = useState({ type: 'All', project: 'All' });
  const [showAddResourceModal, setShowAddResourceModal] = useState(false);
  const [filteredResources, setFilteredResources] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsVisible, setIsNotificationsVisible] = useState(true);
  const [splitActivityMode, setSplitActivityMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredResource, setHoveredResource] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [errorLoadingProjects, setErrorLoadingProjects] = useState(null);

  const containerRef = useRef(null);
  const notificationIdRef = useRef(0);

  const mapResourceTypeToBackend = useCallback((type) => {
    switch(type) {
      case 'Person': return 'user';
      case 'Vehicle': return 'car';
      case 'Machine': return 'machine';
      default: return 'user';
    }
  }, []);

  const addNotification = useCallback((message) => {
    const uniqueId = uuidv4();
    const newNotification = { id: uniqueId, message };
    setNotifications(prev => [...prev, newNotification]);
    setTimeout(() => removeNotification(uniqueId), 5000);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const toggleNotificationsVisibility = useCallback(() => {
    setIsNotificationsVisible(prev => !prev);
  }, []);

  const getResourceIcon = useCallback((type) => {
    if (type === 'Person') return <User size={16} className="mr-2" />;
    if (type === 'Vehicle') return <Truck size={16} className="mr-2" />;
    if (type === 'Machine') return <Wrench size={16} className="mr-2" />;
    return null;
  }, []);

  const renderResourceName = useCallback((resource) => {
    if (resource.type === 'Machine') return resource.machine_type;
    if (resource.type === 'Person') return `${resource.first_name} ${resource.last_name}`;
    if (resource.type === 'Vehicle') return resource.car_brand;
    return resource.name;
  }, []);

  const calculateWorkDays = useCallback((resourceId) => {
    return assignments
      .filter(a => a.resource_id === resourceId && a.type !== 'day_off' && a.type !== 'travel')
      .reduce((total, a) => {
        const start = moment(a.start_date);
        const end = moment(a.end_date);
        return total + end.diff(start, 'days') + 1;
      }, 0);
  }, [assignments]);

  const daysToShow = useMemo(() => {
    switch (viewMode) {
      case 'Week': return 7;
      case '2 Weeks': return 14;
      case 'Month': {
        const startOfMonth = moment(currentDate).startOf('month');
        const endOfMonth = moment(currentDate).endOf('month');
        return getFullWeeksDays(startOfMonth, endOfMonth);
      }
      case '3 Months': {
        const startOfThreeMonths = moment(currentDate).startOf('month');
        const endOfThreeMonths = moment(currentDate).add(3, 'months').endOf('month');
        return getFullWeeksDays(startOfThreeMonths, endOfThreeMonths);
      }
      case '6 Months': {
        const startOfSixMonths = moment(currentDate).startOf('month');
        const endOfSixMonths = moment(currentDate).add(6, 'months').endOf('month');
        return getFullWeeksDays(startOfSixMonths, endOfSixMonths);
      }
      case 'Year': {
        const startOfYear = moment(currentDate).startOf('year');
        const endOfYear = moment(currentDate).endOf('year');
        return getFullWeeksDays(startOfYear, endOfYear);
      }
      default: return 14;
    }
  }, [viewMode, currentDate]);
  
  const fetchUserResources = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_BASE_URL}/user-resources/`, { headers });
      console.log('Fetched user resources:', response.data);
      return response.data.map(user => ({ ...user, type: 'Person' }));
    } catch (error) {
      console.error('Error fetching user resources:', error);
      addNotification('Failed to fetch user resources');
      return [];
    }
  }, [API_BASE_URL, addNotification]);

  const fetchAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const startDate = moment(currentDate).format('YYYY-MM-DD');
      const endDate = moment(currentDate).add(daysToShow - 1, 'days').format('YYYY-MM-DD');
      
      const response = await axios.get(`${API_BASE_URL}/assignments/`, {
        headers,
        params: { start_date: startDate, end_date: endDate }
      });
      
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments', error);
      addNotification('Błąd podczas pobierania przypisań');
    }
  }, [currentDate, daysToShow, API_BASE_URL, addNotification]);

  const handleDragEnd = useCallback((result) => {
    const { destination, source } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const reorderedFilteredResources = Array.from(filteredResources);
    const [movedResource] = reorderedFilteredResources.splice(source.index, 1);
    reorderedFilteredResources.splice(destination.index, 0, movedResource);

    const newResourcesOrder = [...reorderedFilteredResources];

    resources.forEach(resource => {
      if (!reorderedFilteredResources.find(r => r.id === resource.id)) {
        newResourcesOrder.push(resource);
      }
    });

    setResources(newResourcesOrder);
    addNotification('Resources reordered successfully.');
  }, [filteredResources, resources, addNotification]);

  const handlePrevious = useCallback(() => {
    setCurrentDate(prev => moment(prev).subtract(daysToShow, 'days'));
  }, [daysToShow]);

  const handleNext = useCallback(() => {
    setCurrentDate(prev => moment(prev).add(daysToShow, 'days'));
  }, [daysToShow]);

  const handleToday = useCallback(() => {
    setCurrentDate(moment().startOf('isoWeek'));
  }, []);

  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    if (mode === 'Month') {
      const startOfMonth = moment(currentDate).startOf('month').startOf('isoWeek');
      setCurrentDate(startOfMonth);
    } else if (mode === '3 Months') {
      const startOfThreeMonths = moment(currentDate).startOf('month').startOf('isoWeek');
      setCurrentDate(startOfThreeMonths);
    } else if (mode === '6 Months') {
      const startOfSixMonths = moment(currentDate).startOf('month').startOf('isoWeek');
      setCurrentDate(startOfSixMonths);
    } else if (mode === 'Year') {
      const startOfYear = moment(currentDate).startOf('year').startOf('isoWeek');
      setCurrentDate(startOfYear);
    } else {
      setCurrentDate(moment().startOf('isoWeek'));
    }
  }, [currentDate]);

  const handleAssignmentClick = useCallback((assignment) => {
    setSelectedAssignment(assignment);
    setSelectedDate(moment(assignment.start_date));
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [machinesResponse, usersResponse, carsResponse, projectsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/machine/`, { headers }),
        axios.get(`${API_BASE_URL}/users/`, { headers }),
        axios.get(`${API_BASE_URL}/cars/`, { headers }),
        axios.get(`${API_BASE_URL}/project/`, { headers }),
      ]);

      const combinedResources = [
        ...usersResponse.data.map(user => ({ ...user, type: 'Person' })),
        ...carsResponse.data.map(car => ({ ...car, type: 'Vehicle' })),
        ...machinesResponse.data.map(machine => ({ ...machine, type: 'Machine' })),
      ];

      const projectsWithColors = projectsResponse.data.map(project => ({
        ...project,
        color: project.color || assignmentTypeDetails[project.type]?.color(project) || '#3498db',
      }));

      setResources(combinedResources);
      setProjects(projectsWithColors);
      setLoadingProjects(false);

      await fetchAssignments();
    } catch (error) {
      console.error('Error fetching data', error);
      setErrorLoadingProjects('Failed to load data.');
      setLoadingProjects(false);
    }
  }, [fetchAssignments]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAssignments();
  }, [currentDate, viewMode, fetchAssignments]);

  const validateAssignment = (data) => {
    const errors = [];

    if (!data.project) errors.push('Project is required.');

    const validResourceTypes = ['user', 'car', 'machine'];
    if (!data.resource_type || !validResourceTypes.includes(data.resource_type)) {
      errors.push('Invalid or missing resource type.');
    }

    if (!data.resource_id) errors.push('Resource ID is required.');

    const validTypes = ['project', 'travel', 'holiday', 'day_off'];
    if (!data.type || !validTypes.includes(data.type)) {
      errors.push('Invalid assignment type.');
    }

    if (!moment(data.start_date, 'YYYY-MM-DD', true).isValid()) {
      errors.push('Start date is invalid. Use YYYY-MM-DD format.');
    }
    if (!moment(data.end_date, 'YYYY-MM-DD', true).isValid()) {
      errors.push('End date is invalid. Use YYYY-MM-DD format.');
    }

    return errors;
  };

  const saveAssignment = async (assignment) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
  
      const data = {
        project: assignment.project,
        resource_type: assignment.resource_type,
        resource_id: assignment.resource_id,
        start_date: moment(assignment.start_date).format('YYYY-MM-DD'),
        end_date: moment(assignment.end_date).format('YYYY-MM-DD'),
        type: assignment.type,
        color: assignment.color,
        comment: assignment.comment
      };
  
      console.log('Sending assignment data:', data);
  
      const response = await axios.post(`${API_BASE_URL}/assignments/`, data, { headers });
      await fetchAssignments();
      return response.data;
    } catch (error) {
      console.error('Error saving assignment:', error);
      if (error.response && error.response.data) {
        Object.entries(error.response.data).forEach(([key, value]) => {
          addNotification(`Error in ${key}: ${value}`);
        });
      } else {
        addNotification('An unexpected error occurred while saving the assignment.');
      }
      throw error;
    }
  };

  const updateAssignment = useCallback(async (updatedAssignment) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  
      if (!updatedAssignment.resource_type || !updatedAssignment.resource_id) {
        console.error('Error: resource_type or resource_id is undefined in updatedAssignment');
        const originalAssignment = assignments.find(a => a.id === updatedAssignment.id);
        if (originalAssignment) {
          console.log('Found original assignment:', originalAssignment);
          updatedAssignment.resource_type = originalAssignment.resource_type || originalAssignment.resource?.type;
          updatedAssignment.resource_id = originalAssignment.resource_id || originalAssignment.resource?.id;
        } else {
          addNotification('Error: Could not find resource information for this assignment');
          return;
        }
      }
  
      const data = {
        project: updatedAssignment.project,
        resource_type: updatedAssignment.resource_type,
        resource_id: updatedAssignment.resource_id,
        start_date: moment(updatedAssignment.start).format('YYYY-MM-DD'),
        end_date: moment(updatedAssignment.end).format('YYYY-MM-DD'),
        type: updatedAssignment.type,
        color: updatedAssignment.color,
        comment: updatedAssignment.comment
      };
  
      console.log('Updating assignment data:', data);
  
      const response = await axios.put(`${API_BASE_URL}/assignments/${updatedAssignment.id}/`, data, { headers });
      
      await fetchAssignments();
      addNotification('Assignment updated successfully');
    } catch (error) {
      console.error('Error updating assignment:', error);
      if (error.response && error.response.data) {
        console.error('Error response from server:', error.response.data);
        Object.entries(error.response.data).forEach(([key, value]) => {
          addNotification(`Error in ${key}: ${value.join(', ')}`);
        });
      } else {
        addNotification('An unexpected error occurred while updating the assignment');
      }
    }
  }, [addNotification, API_BASE_URL, assignments, fetchAssignments]);

  const deleteAssignment = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_BASE_URL}/assignments/${id}/`, { headers });
      await fetchAssignments();
      setSelectedAssignment(null);
      addNotification('Assignment has been deleted.');
    } catch (error) {
      console.error('Error deleting assignment', error);
      addNotification('Error while deleting assignment');
    }
  }, [addNotification, API_BASE_URL, fetchAssignments]);

  const addNewAssignment = useCallback(async (resourceId, resourceType, day, type = 'project') => {
    const resource = resources.find(res => res.id === resourceId);
    if (!resource) {
      console.error('Resource not found:', resourceId);
      addNotification('Error: Resource not found');
      return;
    }
  
    let color = '#3498db';
    let projectId = null;
  
    if (type === 'project') {
      const defaultProject = projects.find(p => p.type === 'project') || projects[0];
      projectId = defaultProject?.id || null;
      color = defaultProject?.color || '#3498db';
    } else {
      color = assignmentTypeDetails[type]?.color() || '#3498db';
    }
  
    const newAssignment = {
      project: projectId,
      resource_type: mapResourceTypeToBackend(resource.type),
      resource_id: resource.id,
      start_date: day.format('YYYY-MM-DD'),
      end_date: day.format('YYYY-MM-DD'),
      type: type,
      color: color,
      comment: ''
    };
  
    console.log('New assignment data:', newAssignment);
  
    try {
      const savedAssignment = await saveAssignment(newAssignment);
      await fetchAssignments();
      setSelectedAssignment(savedAssignment);
      setSelectedDate(day);
      addNotification(`New assignment added for ${renderResourceName(resource)}.`);
    } catch (error) {
      console.error('Error adding new assignment:', error);
      if (error.response && error.response.data) {
        Object.entries(error.response.data).forEach(([key, value]) => {
          addNotification(`Error in ${key}: ${value}`);
        });
      } else {
        addNotification('An unexpected error occurred while adding new assignment.');
      }
    }
  }, [projects, resources, addNotification, renderResourceName, saveAssignment, mapResourceTypeToBackend, fetchAssignments]);

  const addNewResource = useCallback(async (newResource) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_BASE_URL}/resources/`, newResource, { headers });
      const savedResource = response.data;
      setResources(prev => [...prev, savedResource]);
      setShowAddResourceModal(false);
      addNotification(`New resource added: ${renderResourceName(savedResource)}.`);
    } catch (error) {
      console.error('Error adding new resource', error);
      addNotification('Error while adding new resource');
    }
  }, [addNotification, renderResourceName, API_BASE_URL]);

  const deleteResource = useCallback(async (resourceId) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_BASE_URL}/resources/${resourceId}/`, { headers });
      setResources(prev => prev.filter(r => r.id !== resourceId));
      await fetchAssignments();
      addNotification('Resource has been deleted.');
    } catch (error) {
      console.error('Error deleting resource', error);
      addNotification('Error while deleting resource');
    }
  }, [addNotification, API_BASE_URL, fetchAssignments]);

  useEffect(() => {
    const filtered = resources.filter(resource => 
      (filter.type === 'All' || resource.type === filter.type) &&
      (filter.project === 'All' || assignments.some(a => a.resource_id === resource.id && a.type === filter.project))
    );
    setFilteredResources(filtered);
  }, [resources, assignments, filter]);

  const assignmentsWithSlots = useMemo(() => {
    const resourceSlotsMap = {};
    filteredResources.forEach(resource => {
      const resourceAssignments = assignments
        .filter(a => a.resource_id === resource.id)
        .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)));

      const slots = []; 
      const assignmentSlots = {}; 

      resourceAssignments.forEach(assignment => {
        let placed = false;
        for (let i = 0; i < slots.length; i++) {
          const lastAssignmentInSlot = slots[i][slots[i].length - 1];
          if (moment(lastAssignmentInSlot.end_date).isBefore(moment(assignment.start_date))) {
            slots[i].push(assignment);
            assignmentSlots[assignment.id] = i;
            placed = true;
            break;
          }
        }
        if (!placed) {
          slots.push([assignment]);
          assignmentSlots[assignment.id] = slots.length - 1;
        }
      });

      resourceAssignments.forEach(assignment => {
        assignment.slot = assignmentSlots[assignment.id];
      });

      resourceSlotsMap[resource.id] = {
        maxSlots: slots.length,
        assignments: resourceAssignments
      };
    });
    return resourceSlotsMap;
  }, [assignments, filteredResources]);

  const renderDayHeaders = useMemo(() => {
    const days = [];
    const isMonthView = daysToShow > 14;

    for (let i = 0; i < daysToShow; i++) {
      const day = moment(currentDate).add(i, 'days');
      const isSunday = day.isoWeekday() === 7;
      const isFirstDayOfWeek = day.isoWeekday() === 1;
      const weekNumber = day.isoWeek();

      const cellStyle = isMonthView
        ? { minWidth: `${DAY_WIDTH}px`, width: `${DAY_WIDTH}px` }
        : { flex: 1 };

      days.push(
        <div
          key={i}
          className={`text-center border-r border-gray-300 font-semibold ${
            isSunday ? 'bg-red-100' : ''
          } ${isMonthView ? 'py-0.5' : 'py-2'}`}
          style={cellStyle}
        >
          {isFirstDayOfWeek && !isMonthView && (
            <div className="text-xs text-gray-500"></div>
          )}
          <div className={`${isSunday ? 'text-red-600' : ''} ${isMonthView ? 'text-xxs' : 'text-sm'}`}>
            {isMonthView ? (
              <>
                <div className="text-xs text-gray-500">Week {weekNumber}</div>
                <div className="font-bold">{day.format('DD')}</div>
                <div>{day.format('ddd')}</div>
              </>
            ) : (
              day.format('ddd DD/MM')
            )}
          </div>
        </div>
      );
    }
    return days;
  }, [currentDate, daysToShow]);

  const renderAssignmentsMemo = useCallback((resource) => {
    const resourceSlots = assignmentsWithSlots[resource.id];
    if (!resourceSlots) return null;

    const { maxSlots, assignments: resourceAssignments } = resourceSlots;
    const assignmentHeight = maxSlots > 0 ? 100 / maxSlots : 100;

    const isMonthView = daysToShow > 14;
    const containerWidth = containerRef.current ? containerRef.current.offsetWidth - 256 : DAY_WIDTH * daysToShow;
    const dynamicDayWidth = isMonthView ? DAY_WIDTH : (containerWidth / daysToShow);

    return resourceAssignments
      .filter(a => a.resource_id === resource.id)
      .map(assignment => {
        const startDate = moment(assignment.start_date).startOf('day');
        const endDate = moment(assignment.end_date).endOf('day');
        const startOfView = moment(currentDate).startOf('day');
        const endOfView = moment(currentDate).add(daysToShow - 1, 'days').endOf('day');

        if (endDate.isBefore(startOfView) || startDate.isAfter(endOfView)) {
          return null;
        }

        const leftDays = Math.max(0, startDate.diff(startOfView, 'days'));
        const widthDays = Math.min(daysToShow - leftDays, endDate.diff(startOfView, 'days') - leftDays + 1);

        const left = leftDays * dynamicDayWidth;
        const width = widthDays * dynamicDayWidth;

        const typeDetail = assignmentTypeDetails[assignment.type] || {
          label: 'Project',
          color: '#3498db',
        };

        const project = assignment.type === 'project' ? projects.find(p => p.id === assignment.project) : null;
        const label = assignment.type === 'project' ? (project?.name || 'Project') : typeDetail.label();
        const backgroundColor = assignment.color || (project ? project.color : typeDetail.color(project));

        return (
          <div
            key={assignment.id}
            style={{
              position: 'absolute',
              top: `${assignment.slot * assignmentHeight}%`,
              left: `${left}px`,
              width: `${width}px`,
              height: `${assignmentHeight}%`,
              backgroundColor: backgroundColor,
              color: 'white',
              padding: '2px',
              overflow: 'hidden',
              cursor: 'pointer',
              fontSize: `${10 / zoomLevel}px`,
              borderRadius: '2px',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              zIndex: 1
            }}
            onClick={() => handleAssignmentClick(assignment)}
            title={`${label}: ${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`}
            className="hover:opacity-90 transition-opacity"
          >
            <div>{label}</div>
            {assignment.comment && (
              <div style={{ fontSize: `${10 / zoomLevel}px`, opacity: 0.8 }}>{assignment.comment}</div>
            )}
          </div>
        );
      });
  }, [assignmentsWithSlots, currentDate, daysToShow, handleAssignmentClick, zoomLevel, projects]);

  const renderResourceRows = useMemo(() => {
    return (
      <Droppable droppableId="resources">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {filteredResources.map((resource, index) => {
              const resourceSlots = assignmentsWithSlots[resource.id];
              const maxSlots = resourceSlots ? resourceSlots.maxSlots : 1;
              const rowHeight = `${30}px`;
              const isMonthView = daysToShow > 14;
              const containerWidth = containerRef.current ? containerRef.current.offsetWidth - 256 : DAY_WIDTH * daysToShow;
              const dynamicDayWidth = isMonthView ? DAY_WIDTH : (containerWidth / daysToShow);

              return (
                <Draggable
                  key={`resource-${resource.id}-${index}`}
                  draggableId={`draggable-${resource.id}-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="flex border-b border-gray-300"
                      style={{
                        ...provided.draggableProps.style,
                        minHeight: rowHeight,
                        height: rowHeight
                      }}
                      onMouseEnter={() => setHoveredResource(resource)}
                      onMouseLeave={() => setHoveredResource(null)}
                    >
                      <div className="w-64 p-2 bg-gray-100 flex justify-between items-center sticky left-0 z-10">
                        <div className="flex items-center">
                          {getResourceIcon(resource.type)}
                          <span className="text-sm">{renderResourceName(resource)}</span> 
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs mr-2">
                            Days: {calculateWorkDays(resource.id)}
                          </span>
                          <Trash2 
                            size={16} 
                            className="text-red-500 cursor-pointer" 
                            onClick={() => deleteResource(resource.id)}
                          />
                        </div>
                      </div>
                      <div className="flex-grow relative" style={{ height: rowHeight }}>{daysToShow > 0 && [...Array(daysToShow)].map((_, dayIndex) => {
                          const day = moment(currentDate).add(dayIndex, 'days');
                          const isSunday = day.isoWeekday() === 7;
                          return (
                            <div
                              key={`day-${resource.id}-${index}-${dayIndex}`}
                              className={`border-r border-gray-300 ${
                                isSunday ? 'bg-red-100' : ''
                              }`}
                              style={{ 
                                position: 'absolute',
                                left: `${dayIndex * dynamicDayWidth}px`, 
                                top: 0,
                                bottom: 0,
                                width: `${dynamicDayWidth}px`
                              }}
                              onClick={() => addNewAssignment(resource.id, resource.type, day)}
                            />
                          );
                        })}
                        {renderAssignmentsMemo(resource)}
                      </div>
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }, [
    filteredResources, 
    daysToShow, 
    currentDate,
    deleteResource, 
    addNewAssignment, 
    renderAssignmentsMemo, 
    assignmentsWithSlots, 
    calculateWorkDays, 
    getResourceIcon, 
    renderResourceName
  ]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  }, []);
  
  const handleScroll = useCallback((e) => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = e.target.scrollLeft;
    }
  }, []);

  const dailyProjectSummary = useMemo(() => {
    const summary = {};
    for (let i = 0; i < daysToShow; i++) {
      const day = moment(currentDate).add(i, 'days').format('YYYY-MM-DD');
      summary[day] = {};

      assignments.forEach(assignment => {
        const startDate = moment(assignment.start_date);
        const endDate = moment(assignment.end_date);
        if (moment(day).isBetween(startDate, endDate, null, '[]')) {
          const project = projects.find(p => p.id === assignment.project);
          const projectName = project ? project.name : 'Unknown Project';
          if (!summary[day][projectName]) {
            summary[day][projectName] = 0;
          }
          summary[day][projectName]++;
        }
      });
    }
    return summary;
  }, [assignments, currentDate, daysToShow, projects]);

  const renderDailyProjectSummary = useMemo(() => {
    const isMonthView = daysToShow > 14;
    const containerWidth = containerRef.current ? containerRef.current.offsetWidth - 256 : DAY_WIDTH * daysToShow;
    const dynamicDayWidth = isMonthView? Math.max(DAY_WIDTH, containerWidth / daysToShow): containerWidth / daysToShow;

    return (
      <div className="mt-8 border-t border-gray-300" style={{ display: 'flex', width: daysToShow > 14 ? 'max-content' : '100%' }}>
        {/* Sidebar */}
        <div className="w-64 bg-gray-100 border-r border-gray-300 sticky left-0 z-10">
          <div className="p-4 font-bold text-center">Daily Project Summary</div>
        </div>
        
        {/* Summary Container */}
        <div className="flex-grow overflow-x-auto">
          <div
            className="flex"
            style={{
              minWidth: isMonthView ? `${daysToShow * dynamicDayWidth}px` : '100%',
            }}
          >
            {[...Array(daysToShow)].map((_, dayIndex) => {
              const day = moment(currentDate)
                .add(dayIndex, 'days')
                .format('YYYY-MM-DD');
              const daySummary = dailyProjectSummary[day] || {};

              return (
                <div
                  key={`summary-${dayIndex}`}
                  className="border-r border-gray-300 flex-shrink-0 p-4 hover:bg-gray-50 transition-colors duration-200"
                  style={{
                    width: `${dynamicDayWidth}px`,
                  }}
                >
                  {/* Data */}
                  <div className="text-sm font-semibold mb-2 text-center">
                    {moment(day).format('MMM DD')}
                  </div>
                  <div className="space-y-1">
                    {Object.entries(daySummary).length > 0 ? (
                      Object.entries(daySummary).map(([project, count]) => (
                        <div key={project} className="text-xs text-gray-700">
                          <span className="font-medium text-blue-600">
                            {project}:
                          </span>{' '}
                          {count}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-gray-500">No data</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }, [dailyProjectSummary, currentDate, daysToShow, containerRef]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header />
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Resource Planning</h1>
            <Link 
              to="/ai-assistant" 
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              AI Assistant
            </Link>
          </div>
          
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <div className="flex space-x-2">
                <button onClick={handlePrevious} className="btn btn-blue"><ChevronLeft size={20} /></button>
                <button onClick={handleToday} className="btn btn-blue">Today</button>
                <button onClick={handleNext} className="btn btn-blue"><ChevronRight size={20} /></button>
              </div>
              
              <div className="flex space-x-2">
                {['Week', '2 Weeks', 'Month', '3 Months', '6 Months', 'Year'].map(mode => (
                  <button 
                    key={mode} 
                    onClick={() => changeViewMode(mode)} 
                    className={`btn ${viewMode === mode ? 'btn-blue' : 'btn-gray'}`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <button onClick={handleZoomIn} className="btn btn-blue"><ZoomIn size={20} /></button>
                <button onClick={handleZoomOut} className="btn btn-blue"><ZoomOut size={20} /></button>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Enter new project name"
                className="input"
              />
              
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className="select"
              >
                <option value="All">All Types</option>
                <option value="Person">People</option>
                <option value="Vehicle">Vehicles</option>
                <option value="Machine">Machines</option>
              </select>
              
              <select
                value={filter.project}
                onChange={(e) => setFilter({ ...filter, project: e.target.value })}
                className="select"
              >
                <option value="All">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.type}>
                    {project.name}
                  </option>
                ))}
              </select>
              
              <button onClick={() => setShowAddResourceModal(true)} className="btn btn-green flex items-center">
                <Plus size={16} className="mr-1" />
                Add Resource
              </button>
            </div>
          </div>
          
          <div className=" bg-white shadow rounded-lg overflow-hidden">
            <div 
              ref={containerRef}
              className="overflow-x-auto relative"
              onScroll={handleScroll}
            >
              <div style={{ display: 'flex', width: daysToShow > 14 ? 'max-content' : '100%' }}> 
                <div className="w-64 bg-gray-100 border-r border-gray-300 sticky left-0 z-10">
                  <div className="p-2 font-bold">Resources</div>
                </div>

                <div className="flex flex-1">
                  {renderDayHeaders}
                </div>
              </div>

              <DragDropContext onDragEnd={handleDragEnd}>
                {renderResourceRows}
              </DragDropContext>

              {renderDailyProjectSummary}
            </div>
          </div>
        </div>
      </main>

      {selectedAssignment && (
        <EditAssignmentModal
          assignment={selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
          onSave={updateAssignment}
          onDelete={deleteAssignment}
          projects={projects}
        />
      )}

      {showAddResourceModal && (
        <AddResourceModal
          onClose={() => setShowAddResourceModal(false)}
          onAdd={addNewResource}
        />
      )}

      <NotificationContainer 
        notifications={notifications}
        onClose={removeNotification}
        isVisible={isNotificationsVisible}
        onToggle={toggleNotificationsVisibility}
      />

      {hoveredResource && hoveredResource.type === 'Person' && (
        <ResourceInfoPopup resource={hoveredResource} />
      )}
    </div>
  );
};

export default Resources;